import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STYLES = {
  gold: {
    background: 'linear-gradient(135deg, #FDF6E3 0%, #FBE9B7 55%, #F5D97F 100%)',
    boxShadow: '0 4px 16px rgba(184, 134, 11, 0.18)',
    border: '1px solid rgba(184, 134, 11, 0.35)',
    accent: '#8B6914',
    accentBg: 'rgba(184, 134, 11, 0.15)',
    accentBorder: '#B8860B',
    labelBg: 'rgba(255,255,255,0.6)',
    labelColor: '#B8860B',
    titleColor: '#3D2E00',
    ctaBg: '#8B6914',
  },
  emerald: {
    background: 'linear-gradient(135deg, #E6F4EA 0%, #A7D7B7 55%, #4C9B6E 100%)',
    boxShadow: '0 4px 16px rgba(30, 90, 60, 0.18)',
    border: '1px solid rgba(30, 90, 60, 0.35)',
    accent: '#1F5A3C',
    accentBg: 'rgba(30, 90, 60, 0.15)',
    accentBorder: '#1F5A3C',
    labelBg: 'rgba(255,255,255,0.6)',
    labelColor: '#1F5A3C',
    titleColor: '#0E2A1C',
    ctaBg: '#1F5A3C',
  },
  urgency_red: {
    background: 'linear-gradient(135deg, #FDECEA 0%, #F8B4AB 55%, #E74C3C 100%)',
    boxShadow: '0 4px 16px rgba(200, 30, 30, 0.20)',
    border: '1px solid rgba(200, 30, 30, 0.35)',
    accent: '#8B1F1F',
    accentBg: 'rgba(200, 30, 30, 0.15)',
    accentBorder: '#C0392B',
    labelBg: 'rgba(255,255,255,0.7)',
    labelColor: '#C0392B',
    titleColor: '#4A0F0F',
    ctaBg: '#8B1F1F',
  },
  custom: {
    background: 'linear-gradient(135deg, #F0F0F0 0%, #D0D0D0 55%, #A0A0A0 100%)',
    boxShadow: '0 4px 16px rgba(80,80,80,0.18)',
    border: '1px solid rgba(80,80,80,0.35)',
    accent: '#333',
    accentBg: 'rgba(0,0,0,0.06)',
    accentBorder: '#666',
    labelBg: 'rgba(255,255,255,0.6)',
    labelColor: '#333',
    titleColor: '#111',
    ctaBg: '#333',
  },
};

function DealCard({ deal, onClick, sectionLabel = 'Le coin des affaires' }) {
  const s = STYLES[deal.style] || STYLES.gold;

  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-2xl px-4 py-4 relative overflow-hidden group active:scale-[0.99] transition-all"
      style={{ background: s.background, boxShadow: s.boxShadow, border: s.border }}
      data-testid={`featured-deal-${deal.id}`}
    >
      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-30" style={{ background: `radial-gradient(circle, ${s.accentBorder} 0%, transparent 70%)` }} />
      <div className="relative flex items-center gap-3">
        <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: s.accentBg, border: `1.5px solid ${s.accentBorder}` }}>
          <Sparkles size={18} style={{ color: s.accent }} strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          {/* Ligne 1 : "Bonnes affaires" (label section) + badge % */}
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <p className="font-bold uppercase tracking-wider" style={{ fontSize: '10px', color: s.accent, letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>
              {sectionLabel}
            </p>
            {deal.subtitle && (
              <span className="font-bold px-2 py-0.5 rounded-md flex-shrink-0" style={{ fontSize: '10px', color: s.labelColor, backgroundColor: s.labelBg, letterSpacing: '0.06em' }} data-testid={`featured-deal-subtitle-${deal.id}`}>
                {deal.subtitle}
              </span>
            )}
          </div>
          {/* Ligne 2 : Titre du deal (nom de la promo) */}
          <p className="font-semibold leading-tight truncate" style={{ fontSize: '14px', color: s.titleColor }}>
            {deal.title}
          </p>
          {/* Ligne 3 : Description discrète */}
          {deal.description && (
            <p className="truncate mt-0.5" style={{ fontSize: '11px', color: s.accent, opacity: 0.85 }}>
              {deal.description}
            </p>
          )}
        </div>
        <span className="font-semibold text-white px-3.5 py-2 flex-shrink-0 group-hover:scale-105 transition-transform whitespace-nowrap" style={{ fontSize: '11px', backgroundColor: s.ctaBg, borderRadius: '20px' }}>
          {deal.cta_label || 'Réserver'}
        </span>
      </div>
    </button>
  );
}

/**
 * Carrousel de bonnes affaires
 * Props:
 *  - endpoint: URL relative de fetch (ex: '/api/client-portal/featured-deals')
 *  - authHeader: bool, si true ajoute Authorization Bearer token
 *  - authToken: string (portail utilise 'client_token', admin utilise 'access_token'/'token')
 *  - onDealClick: (deal) => void — callback si on veut router côté React
 *  - rotationMs: int (défaut 6000)
 */
export default function FeaturedDealsCarousel({
  endpoint,
  authToken = null,
  onDealClick,
  rotationMs = 6000,
  emptyFallback = null,
  title = 'Bonnes affaires',
  showTitle = true,
}) {
  const [deals, setDeals] = useState([]);
  const [index, setIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const { data } = await axios.get(`${API}${endpoint}`, { headers });
      setDeals(Array.isArray(data) ? data : []);
      setLoaded(true);
    } catch {
      setDeals([]);
      setLoaded(true);
    }
  }, [endpoint, authToken]);

  useEffect(() => { load(); }, [load]);

  // Rotation auto
  useEffect(() => {
    if (deals.length <= 1) return;
    const t = setInterval(() => setIndex(i => (i + 1) % deals.length), rotationMs);
    return () => clearInterval(t);
  }, [deals.length, rotationMs]);

  // Track impression au changement de deal visible
  useEffect(() => {
    if (!deals[index] || !deals[index].id) return;
    const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
    axios.post(`${API}/api/featured-deals/${deals[index].id}/impression`, {}, { headers }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deals, index]);

  if (!loaded) return null;
  if (deals.length === 0) return emptyFallback || null;

  const current = deals[index];

  const handleClick = () => {
    if (current.id) {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      axios.post(`${API}/api/featured-deals/${current.id}/click`, {}, { headers }).catch(() => {});
    }
    if (onDealClick) {
      onDealClick(current);
    } else if (current.cta_url) {
      if (current.cta_url.startsWith('portal://book/indiv/')) {
        // Handled by onDealClick generally, but fallback
        return;
      }
      if (current.cta_url.startsWith('http')) {
        window.open(current.cta_url, '_blank', 'noopener');
      } else {
        window.location.href = current.cta_url;
      }
    }
  };

  return (
    <div className="space-y-2" data-testid="featured-deals-carousel">
      <DealCard deal={current} onClick={handleClick} sectionLabel={title} />
      {deals.length > 1 && (
        <div className="flex items-center justify-center gap-1.5" data-testid="carousel-dots">
          {deals.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all ${i === index ? 'w-6 bg-neutral-700' : 'w-1.5 bg-neutral-300'}`}
              aria-label={`Bonne affaire ${i + 1}`}
              data-testid={`carousel-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
